import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  CheckCircle2, 
  XCircle, 
  AlertCircle, 
  User, 
  Building2, 
  Armchair,
  RefreshCw,
  ExternalLink
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

interface HealthCheckItem {
  id: string;
  label: string;
  description: string;
  status: "ok" | "warning" | "error" | "loading";
  details?: string;
  action?: {
    label: string;
    href: string;
  };
}

interface AccountHealthCheckProps {
  userId: string;
  profileExists: boolean;
}

export function AccountHealthCheck({ userId, profileExists }: AccountHealthCheckProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [workspaceMembership, setWorkspaceMembership] = useState<{
    exists: boolean;
    workspaceName?: string;
    role?: string;
    seatStatus?: string;
  } | null>(null);

  const fetchHealthData = async () => {
    try {
      // Fetch workspace membership data
      const { data: memberships, error } = await supabase
        .from("workspace_members")
        .select(`
          role,
          seat_status,
          workspaces (
            name
          )
        `)
        .eq("user_id", userId)
        .limit(1);

      if (error) throw error;

      if (memberships && memberships.length > 0) {
        const membership = memberships[0];
        setWorkspaceMembership({
          exists: true,
          workspaceName: (membership.workspaces as any)?.name || "Unbekannt",
          role: membership.role,
          seatStatus: membership.seat_status,
        });
      } else {
        setWorkspaceMembership({ exists: false });
      }
    } catch (err) {
      console.error("Error fetching health data:", err);
      setWorkspaceMembership({ exists: false });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchHealthData();
  }, [userId]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchHealthData();
  };

  const getHealthItems = (): HealthCheckItem[] => {
    if (isLoading) {
      return [
        { id: "profile", label: "Profil", description: "Wird geprüft...", status: "loading" },
        { id: "workspace", label: "Workspace", description: "Wird geprüft...", status: "loading" },
        { id: "seat", label: "Seat-Status", description: "Wird geprüft...", status: "loading" },
      ];
    }

    const items: HealthCheckItem[] = [];

    // Profile check
    items.push({
      id: "profile",
      label: "Profil",
      description: profileExists ? "Profil ist vollständig eingerichtet" : "Profil fehlt oder ist unvollständig",
      status: profileExists ? "ok" : "error",
      details: profileExists ? "Deine Profildaten sind gespeichert." : "Dein Profil konnte nicht geladen werden.",
    });

    // Workspace membership check
    if (workspaceMembership?.exists) {
      items.push({
        id: "workspace",
        label: "Workspace",
        description: `Mitglied in „${workspaceMembership.workspaceName}"`,
        status: "ok",
        details: `Rolle: ${getRoleLabel(workspaceMembership.role || "member")}`,
      });
    } else {
      items.push({
        id: "workspace",
        label: "Workspace",
        description: "Keine Workspace-Mitgliedschaft gefunden",
        status: "warning",
        details: "Du bist noch keinem Workspace beigetreten.",
        action: {
          label: "Workspace erstellen",
          href: "/onboarding",
        },
      });
    }

    // Seat status check
    if (workspaceMembership?.exists) {
      const seatActive = workspaceMembership.seatStatus === "active";
      items.push({
        id: "seat",
        label: "Seat-Status",
        description: seatActive ? "Seat ist aktiv" : "Seat ist deaktiviert",
        status: seatActive ? "ok" : "error",
        details: seatActive 
          ? "Du hast vollen Zugriff auf alle Funktionen." 
          : "Dein Zugang wurde eingeschränkt. Kontaktiere deinen Workspace-Admin.",
        action: !seatActive ? {
          label: "Team kontaktieren",
          href: "/settings/team",
        } : undefined,
      });
    } else {
      items.push({
        id: "seat",
        label: "Seat-Status",
        description: "Kein Seat zugewiesen",
        status: "warning",
        details: "Erstellen oder treten Sie einem Workspace bei.",
      });
    }

    return items;
  };

  const getRoleLabel = (role: string): string => {
    const roles: Record<string, string> = {
      owner: "Eigentümer",
      admin: "Administrator",
      member: "Mitglied",
      viewer: "Betrachter",
    };
    return roles[role] || role;
  };

  const getStatusIcon = (status: HealthCheckItem["status"]) => {
    switch (status) {
      case "ok":
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case "warning":
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case "error":
        return <XCircle className="h-5 w-5 text-destructive" />;
      case "loading":
        return <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground animate-spin" />;
    }
  };

  const getItemIcon = (id: string) => {
    switch (id) {
      case "profile":
        return <User className="h-4 w-4" />;
      case "workspace":
        return <Building2 className="h-4 w-4" />;
      case "seat":
        return <Armchair className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const healthItems = getHealthItems();
  const overallStatus = healthItems.every(item => item.status === "ok") 
    ? "ok" 
    : healthItems.some(item => item.status === "error") 
      ? "error" 
      : "warning";

  const getOverallLabel = () => {
    if (isLoading) return "Wird geprüft...";
    switch (overallStatus) {
      case "ok":
        return "Alles in Ordnung";
      case "warning":
        return "Teilweise eingerichtet";
      case "error":
        return "Probleme erkannt";
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="text-lg flex items-center gap-2">
              Account-Status
              <Badge 
                variant={overallStatus === "ok" ? "default" : overallStatus === "warning" ? "secondary" : "destructive"}
                className={cn(
                  "font-normal",
                  overallStatus === "ok" && "bg-green-500/10 text-green-600 hover:bg-green-500/20 border-green-500/20"
                )}
              >
                {getOverallLabel()}
              </Badge>
            </CardTitle>
            <CardDescription>
              Überprüfung deiner Kontoeinstellungen
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="h-8 w-8"
          >
            <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
            <span className="sr-only">Aktualisieren</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {healthItems.map((item) => (
          <div
            key={item.id}
            className={cn(
              "flex items-start gap-3 p-3 rounded-lg border transition-colors",
              item.status === "ok" && "bg-green-500/5 border-green-500/20",
              item.status === "warning" && "bg-yellow-500/5 border-yellow-500/20",
              item.status === "error" && "bg-destructive/5 border-destructive/20",
              item.status === "loading" && "bg-muted/50 border-border"
            )}
          >
            <div className="mt-0.5">
              {getStatusIcon(item.status)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                {getItemIcon(item.id)}
                <span className="font-medium text-sm">{item.label}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">
                {item.status === "loading" ? (
                  <Skeleton className="h-4 w-40" />
                ) : (
                  item.description
                )}
              </p>
              {item.details && item.status !== "loading" && (
                <p className="text-xs text-muted-foreground mt-1">
                  {item.details}
                </p>
              )}
              {item.action && item.status !== "loading" && (
                <Link
                  to={item.action.href}
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
                >
                  {item.action.label}
                  <ExternalLink className="h-3 w-3" />
                </Link>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
